import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg" | "icon";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

/**
 * Four variants, and the restraint is still the point.
 *
 * Every one of them is a flat fill or a hairline outline — no gradients, no
 * resting glow. A control that lights itself up competes with the data next to
 * it, and on a page with six buttons the effect cancels out anyway.
 *
 * Hierarchy comes from fill weight: a solid indigo fill reads as the one
 * action, a hairline outline as an alternative, ghost as incidental. Sizes are
 * all even so a row of mixed variants lines up on the baseline.
 */
const VARIANTS: Record<Variant, string> = {
  primary: "bg-brand text-brand-content font-semibold shadow-xs hover:bg-brand-hover",
  secondary: cn(
    "bg-surface text-content border border-border shadow-xs",
    "hover:bg-surface-overlay hover:border-border-strong",
  ),
  ghost: "text-content-muted hover:text-content hover:bg-surface-overlay",
  // Reserved for irreversible actions — bans, revokes, deletes. Using it for an
  // ordinary "remove filter" trains people to click red without reading. Its
  // fill comes from `danger-fill`, not `danger`: see the token comment for why
  // the badge red and the button red cannot be the same value.
  danger: "bg-danger-fill text-white shadow-xs hover:bg-danger-fill-hover",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 gap-1.5 rounded-control px-2.5 text-xs",
  md: "h-9 gap-2 rounded-control px-3.5 text-sm",
  lg: "h-10 gap-2 rounded-control px-4 text-sm",
  // Square, for a lone glyph. Padding-based sizing leaves icon buttons subtly
  // non-square and a row of them visibly uneven.
  icon: "size-9 rounded-control",
};

/**
 * React 19: no `forwardRef`. `ref` is an ordinary prop now, so it flows through
 * `...props` to the DOM node without a wrapper.
 */
export function Button({ variant = "primary", size = "md", className, ...props }: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap",
        "transition-all duration-200 ease-out-expo",
        // A press that moves is worth more than a press that changes colour:
        // it is the one feedback that survives being clicked with the cursor
        // covering the control.
        "active:scale-[0.97]",
        "disabled:pointer-events-none disabled:opacity-45",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    />
  );
}
